// Email verification landing — pulls `?token=...` from the URL and
// posts to /merchant/auth/verify-email. Shows a success or failure
// state and a link back to login.

import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, merchantAuth } from '@/api/client';
import { Logo } from '@/components/brand/logo';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

export const Route = createFileRoute('/m/verify-email')({
  component: MerchantVerifyEmail,
});

function MerchantVerifyEmail() {
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'ok' | 'err'>('idle');
  const [errMsg, setErrMsg] = useState('');

  const submit = useMutation({
    mutationFn: (tok: string) => merchantAuth.verifyEmail(tok),
    onSuccess: () => setState('ok'),
    onError: (e) => {
      setState('err');
      setErrMsg(e instanceof ApiError ? e.message : 'Verification failed');
    },
  });

  useEffect(() => {
    const u = new URL(window.location.href);
    const tok = u.searchParams.get('token');
    if (!tok) {
      setState('err');
      setErrMsg(
        t('merchant.verify_email.no_token', { defaultValue: 'No token provided.' }),
      );
      return;
    }
    setToken(tok);
    submit.mutate(tok);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="auth-backdrop min-h-screen grid place-items-center p-4">
      <Card className="w-full max-w-sm shadow-elev">
        <CardHeader className="items-center text-center space-y-3 pb-4">
          <Logo size="xl" className="drop-shadow-sm" />
          <div className="space-y-1">
            <CardTitle className="text-xl">
              {t('merchant.verify_email.title', { defaultValue: 'Email verification' })}
            </CardTitle>
            <CardDescription>
              {state === 'idle' && t('common.loading', { defaultValue: 'Verifying…' })}
              {state === 'ok' &&
                t('merchant.verify_email.ok', { defaultValue: 'Email verified.' })}
              {state === 'err' && (errMsg || t('common.error'))}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button asChild className="w-full">
            <Link to="/m/login">{t('auth.sign_in')}</Link>
          </Button>
          {token && state === 'err' && (
            <p className="text-xs text-muted-foreground text-center">
              {t('merchant.verify_email.try_again', {
                defaultValue: 'Re-send a verification link from your profile page.',
              })}
            </p>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
